import type { AnalyticsModule } from "./analytics";

// Task G1 -- captures a visitor-triggered backend error (the host page's
// OWN API returning 4xx/5xx), the input ai-service's recurring_backend_error
// Signal (G4) is built from. No existing fetch/XHR interception anywhere in
// this widget (confirmed via grep) -- this is from-scratch code, same as
// autofixProbe.ts's visibility check had to be.
//
// Dedup is in-memory, per (status_code, endpoint_path, error_code), per
// page load -- bounds a repeatedly-failing call's noise within a single
// visit without autofixProbe.ts's "forever, per visitor" localStorage
// semantics (a backend error is a recurring pattern to flag over time,
// not a one-shot verdict worth remembering across sessions).

interface ParsedError {
  errorCode: string | null;
  errorMessage: string | null;
}

function stringifyBodyValue(value: unknown): string | null {
  if (typeof value === "string") return value.slice(0, 500);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null; // an object/array-shaped error is left as null -- see parseErrorBody
}

/** Best-effort and shallow on purpose: only known top-level JSON keys,
 * never the full body, never a request body, never recurses into nested
 * structures -- an object-shaped `error`/`detail` is left as null rather
 * than stringified, since there's no safe generic way to summarize an
 * arbitrary nested shape without risking real user data riding along. */
function parseErrorBodyJson(body: unknown): ParsedError {
  if (!body || typeof body !== "object") return { errorCode: null, errorMessage: null };
  const record = body as Record<string, unknown>;
  return {
    errorCode: stringifyBodyValue(record.error_code ?? record.code),
    errorMessage: stringifyBodyValue(record.message ?? record.error ?? record.detail),
  };
}

function endpointPathFrom(url: string): string | null {
  try {
    return new URL(url, location.href).pathname;
  } catch {
    return null;
  }
}

type Report = (statusCode: number, url: string, parsed: ParsedError) => void;

function patchFetch(report: Report): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (...args: Parameters<typeof fetch>) => {
    return originalFetch(...args).then(async (response) => {
      if (response.status >= 400) {
        let parsed: ParsedError = { errorCode: null, errorMessage: null };
        try {
          parsed = parseErrorBodyJson(await response.clone().json());
        } catch {
          // Not a JSON body -- still a useful capture (status + path alone).
        }
        report(response.status, response.url, parsed);
      }
      return response;
    });
  };
}

function patchXhr(report: Report): void {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest & { __artisUrl?: string },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__artisUrl = url.toString();
    return originalOpen.apply(this, [method, url, ...rest] as Parameters<typeof originalOpen>);
  };

  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest & { __artisUrl?: string },
    ...args: unknown[]
  ) {
    this.addEventListener("loadend", () => {
      if (this.status < 400) return;
      let parsed: ParsedError = { errorCode: null, errorMessage: null };
      try {
        parsed = parseErrorBodyJson(JSON.parse(this.responseText));
      } catch {
        // Not a JSON body -- still a useful capture (status + path alone).
      }
      const url = this.__artisUrl ? new URL(this.__artisUrl, location.href).toString() : location.href;
      report(this.status, url, parsed);
    });
    return originalSend.apply(this, args as Parameters<typeof originalSend>);
  };
}

export function startBackendErrorCapture(apiUrl: string, analytics: AnalyticsModule): void {
  const seen = new Set<string>();

  const report: Report = (statusCode, url, parsed) => {
    if (url.startsWith(apiUrl)) return; // never the widget's own traffic/errors
    const endpointPath = endpointPathFrom(url);
    if (!endpointPath) return;
    const key = `${statusCode}:${endpointPath}:${parsed.errorCode ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    analytics.captureBackendError(statusCode, endpointPath, parsed.errorCode, parsed.errorMessage);
  };

  patchFetch(report);
  patchXhr(report);
}
